import { SchemaAndRules } from '@frontend/pricing-search';
import { cloneDeep } from 'lodash';

// The one field the custom search configurator does not expose.
const LOCK_PERIOD_KEY = 'lockDays';

type SchemaField = Record<string, any>;

// Fields sit under screens, then nested field groups.
const eachField = (
  schema: SchemaField | null | undefined,
  visit: (field: SchemaField) => void,
) => {
  const walk = (fields: SchemaField[] = []) => {
    fields.forEach((field) => {
      if (field?.fields?.length) walk(field.fields);
      else visit(field);
    });
  };
  (schema?.schema?.screens ?? []).forEach((screen: SchemaField) =>
    walk(screen?.fields),
  );
};

// SchemaSearchForm keys its Controller on modelPath.
const fieldKey = (field: SchemaField): string | undefined =>
  field?.encompass_ids?.[0]?.modelPath ?? field?.component_id;

// Takes the search form's configuration for the same fields. Layout and field
// level visibility stay local.
export const applySearchFormRules = (
  simpleSearch: SchemaAndRules,
  searchForm: SchemaAndRules | null | undefined,
  lockPeriodDisabled: boolean,
): SchemaAndRules => {
  const configured = new Map<string, SchemaField>();
  eachField(searchForm as unknown as SchemaField, (field) => {
    const key = fieldKey(field);
    if (key) configured.set(key, field);
  });

  const next = cloneDeep(simpleSearch);
  eachField(next as unknown as SchemaField, (field) => {
    const key = fieldKey(field);
    if (key === LOCK_PERIOD_KEY) {
      // readOnly keeps the menu open and the values visible.
      field.readOnly = lockPeriodDisabled;
      return;
    }
    const match = key ? configured.get(key) : undefined;
    if (!match) return;
    field.disabled = match.disabled;
    field.disable_check = match.disable_check;
    // Options carry the visible_check that narrows one field against another.
    if (match.options) field.options = cloneDeep(match.options);
    if (match.selectMultiple !== undefined) {
      field.selectMultiple = match.selectMultiple;
    }
    if (match.isNonClearable !== undefined) {
      field.isNonClearable = match.isNonClearable;
    }
  });

  // Rules travel with the checks, since an unfound id counts as a match.
  // Ids can collide, and ours win.
  const own = simpleSearch.schemaRules ?? [];
  const ownIds = new Set(own.map((rule) => rule?.id));
  next.schemaRules = [
    ...own,
    ...(searchForm?.schemaRules ?? []).filter((rule) => !ownIds.has(rule?.id)),
  ];

  return next;
};
